import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';

// Stable, compact emoji reaction bar shown under each mugshot.
const EMOJIS = [
  { key: 'thumbs_up', char: '👍', label: 'Thumbs up' },
  { key: 'thumbs_down', char: '👎', label: 'Thumbs down' },
  { key: 'heart', char: '❤️', label: 'Heart' },
  { key: 'fire', char: '🔥', label: 'Fire' },
  { key: 'eggplant', char: '🍆', label: 'Eggplant' },
  { key: 'splash', char: '💦', label: 'Splash' },
];

// Resolve a persistent voter id: prefer nickname, else an anonymous local id.
const getVoter = (nickname) => {
  if (nickname && nickname.trim()) return nickname.trim();
  let anon = localStorage.getItem('jb_anon_id');
  if (!anon) {
    anon = 'anon_' + Math.random().toString(36).slice(2, 11);
    localStorage.setItem('jb_anon_id', anon);
  }
  return anon;
};

const emptyCounts = EMOJIS.reduce((acc, e) => ({ ...acc, [e.key]: 0 }), {});

const ReactionBar = ({ recordId, nickname }) => {
  const [counts, setCounts] = useState(emptyCounts);
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(null);
  const voter = getVoter(nickname);

  useEffect(() => {
    if (!recordId) return;
    let active = true;
    setCounts(emptyCounts);
    setMine([]);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mugshots/reactions/${encodeURIComponent(recordId)}?voter=${encodeURIComponent(voter)}`)
      .then((r) => r.json())
      .then((d) => {
        if (active && d.success) {
          setCounts(d.counts || emptyCounts);
          setMine(d.my_reactions || []);
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [recordId, voter]);

  const toggle = useCallback(async (emoji) => {
    if (!recordId || busy) return;
    setBusy(emoji);
    // Optimistic update
    const wasActive = mine.includes(emoji);
    setMine((m) => (wasActive ? m.filter((x) => x !== emoji) : [...m, emoji]));
    setCounts((c) => ({ ...c, [emoji]: Math.max(0, (c[emoji] || 0) + (wasActive ? -1 : 1)) }));
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mugshots/reactions/${encodeURIComponent(recordId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji, voter }),
      });
      const d = await res.json();
      if (d.success) {
        setCounts(d.counts || emptyCounts);
        setMine(d.my_reactions || []);
      }
    } catch (e) {
      // Revert on failure
      setMine((m) => (wasActive ? [...m, emoji] : m.filter((x) => x !== emoji)));
      setCounts((c) => ({ ...c, [emoji]: Math.max(0, (c[emoji] || 0) + (wasActive ? 1 : -1)) }));
    } finally {
      setBusy(null);
    }
  }, [recordId, mine, voter, busy]);

  if (!recordId) return null;

  return (
    <div
      data-testid="reaction-bar"
      className="flex items-center justify-center gap-1 sm:gap-1.5 mt-3 flex-wrap"
    >
      {EMOJIS.map((e) => {
        const isActive = mine.includes(e.key);
        const count = counts[e.key] || 0;
        return (
          <motion.button
            key={e.key}
            data-testid={`reaction-${e.key}`}
            onClick={() => toggle(e.key)}
            whileTap={{ scale: 0.85 }}
            aria-label={e.label}
            className={`flex items-center gap-1 pl-1.5 pr-2 py-1 rounded-full border text-sm leading-none transition-colors ${
              isActive
                ? 'bg-primary/20 border-primary/60 text-primary'
                : 'bg-muted/40 border-border hover:bg-muted/70'
            }`}
          >
            <span className="text-base">{e.char}</span>
            <span className={`text-xs font-semibold tabular-nums ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
              {count}
            </span>
          </motion.button>
        );
      })}
    </div>
  );
};

export default ReactionBar;
